import React, { useState } from "react";
import { buildNovelProductionSourceManifest, hasReadableNovelSavedSourceText } from "../utils/novelProductionSourceManifest";
import { novelProofTrim } from "./NovelStoryPublicationProof";
import NovelManuscriptSnapshotVerification from "./NovelManuscriptSnapshotVerification";
import { buildNovelInteriorProductionPlanV3 } from "../utils/novelInteriorProductionPlanV3";
import { buildNovelInteriorPhysicalLayoutV3B } from "../utils/novelInteriorPhysicalLayoutV3B";
import NovelPhysicalPaginationStudyV3C2Panel from "./NovelPhysicalPaginationStudyV3C2Panel";
import NovelPublicationSimpleOverviewV3C3 from "./NovelPublicationSimpleOverviewV3C3";

// Textbook-derived source checks only. Novel has no verified final-file backend.
const clean = (value) => String(value ?? "").trim();
const safeArray = (value) => Array.isArray(value) ? value : [];

export function novelPublicationPreparationChecks({
  bookFormat = {}, frontCoverUrl = "", backCoverUrl = "",
  chapters = [], sceneById, unassignedSceneIds = [],
} = {}) {
  const savedFormat = bookFormat && typeof bookFormat === "object" ? bookFormat : {};
  const publicationFormat = clean(savedFormat.publicationFormat);
  const digitalOnly = publicationFormat === "digital";
  const trim = novelProofTrim(savedFormat);
  // Accept square orientation only for a measurable equal-width/height trim.
  const savedOrientation = clean(savedFormat.orientation);
  const orientationReady = ["portrait", "landscape"].includes(savedOrientation) ||
    (savedOrientation === "square" && trim.measurable && trim.width === trim.height);
  const formatReady = Boolean(publicationFormat && (digitalOnly ||
    (trim.measurable && orientationReady &&
      ["yes", "no"].includes(clean(savedFormat.bleed)))));
  const chapterList = safeArray(chapters);
  const seenIds = new Set();
  const chapterIssues = [];
  let assignedScenes = 0;
  chapterList.forEach((chapter, index) => {
    const chapterName = clean(chapter.title) || "Chapter " + (index + 1);
    const ids = safeArray(chapter.sceneIds);
    if (!clean(chapter.title)) chapterIssues.push(chapterName + ": missing title");
    if (!ids.length) chapterIssues.push(chapterName + ": no saved Scenes");
    ids.forEach((id) => {
      const key = clean(id);
      assignedScenes += 1;
      if (seenIds.has(key)) chapterIssues.push(chapterName + ": duplicate Scene assignment");
      seenIds.add(key);
      if (!hasReadableNovelSavedSourceText(sceneById?.get(key))) {
        chapterIssues.push(chapterName + ": missing or empty approved Canon Scene");
      }
    });
  });
  if (!chapterList.length) chapterIssues.push("No saved Chapters");
  const unassigned = safeArray(unassignedSceneIds);
  const checks = [
    {
      id: "format", label: "Book format", ready: formatReady, destination: "setup",
      detail: formatReady ? (digitalOnly ? "Digital format saved" : trim.label + "; orientation and bleed saved")
        : "Choose publication format and, for print, measurable trim, orientation and bleed in Book Setup",
    },
    {
      id: "manuscript", label: "Saved Canon manuscript", ready: chapterIssues.length === 0,
      destination: "book-structure",
      detail: chapterIssues.length
        ? chapterIssues.slice(0, 3).join(" · ") + (chapterIssues.length > 3 ? " · more issues" : "")
        : chapterList.length + " Chapters · " + assignedScenes + " assigned Scenes",
    },
    {
      id: "front-cover", label: "Front cover",
      ready: digitalOnly || Boolean(clean(frontCoverUrl)), destination: "setup",
      detail: clean(frontCoverUrl) ? "Saved front cover" :
        digitalOnly ? "Optional for digital-only checks" : "Add a front cover in Book Setup",
    },
    {
      id: "back-cover", label: "Back cover",
      ready: digitalOnly || Boolean(clean(backCoverUrl)), destination: "setup",
      detail: clean(backCoverUrl) ? "Saved back cover" :
        digitalOnly ? "Not required for digital-only checks" : "Add a back cover in Book Setup",
    },
    {
      id: "unassigned", label: "Unassigned Canon Scenes",
      ready: unassigned.length === 0, destination: "book-structure",
      detail: unassigned.length
        ? unassigned.length + " approved Canon Scenes are excluded from the assembled manuscript; assign or resolve them"
        : "No unassigned Canon Scenes are omitted",
    },
  ];
  return {
    checks, passed: checks.filter((check) => check.ready).length,
    total: checks.length, readyForSourceReview: checks.every((check) => check.ready),
    assignedScenes, chapters: chapterList.length, unassigned: unassigned.length, trim,
  };
}

// Advisory only. These six keys match the current shared Book Production
// paperback print-profile trim registry; other measurable Novel proof trims
// are not silently promoted into supported physical production formats.
const SUPPORTED_PAPERBACK_TRIMS = Object.freeze({
  "5.5_x_8.5_in": { key: "5.5x8.5", orientation: "portrait" },
  "6_x_9_in": { key: "6x9", orientation: "portrait" },
  "8.25_x_8.25_in": { key: "8.25x8.25", orientation: "square" },
  "8.5_x_8.5_in": { key: "8.5x8.5", orientation: "square" },
  "8_x_10_in": { key: "8x10", orientation: "portrait" },
  "8.5_x_11_in": { key: "8.5x11", orientation: "portrait" },
});

export function novelPrintSpecificationsPlanning(bookFormat = {}) {
  const saved = bookFormat && typeof bookFormat === "object" && !Array.isArray(bookFormat)
    ? bookFormat : {};
  const publicationFormat = clean(saved.publicationFormat);
  const printRequested = publicationFormat === "print" || publicationFormat === "print_and_digital";
  const digitalOnly = publicationFormat === "digital";
  const trimCode = clean(saved.trimSize);
  const supportedTrim = Object.prototype.hasOwnProperty.call(SUPPORTED_PAPERBACK_TRIMS, trimCode)
    ? SUPPORTED_PAPERBACK_TRIMS[trimCode] : null;
  const orientation = clean(saved.orientation);
  const bleed = clean(saved.bleed);
  const binding = clean(saved.binding);
  const paperType = clean(saved.paperType);
  const interiorColor = clean(saved.interiorColor);
  const paperInteriorCompatible = !paperType || !interiorColor ||
    interiorColor === "black_and_white" || paperType === "white";
  const settingsValid = (!binding || binding === "paperback") &&
    (!paperType || ["white", "cream"].includes(paperType)) &&
    (!interiorColor || ["black_and_white", "standard_color", "premium_color"].includes(interiorColor)) &&
    paperInteriorCompatible;
  const missingSettings = printRequested ? [
    ["binding", "Binding"], ["paperType", "Paper Type"], ["interiorColor", "Interior Colour"],
  ].filter(([key]) => !clean(saved[key])).map(([, name]) => name) : [];
  return {
    printRequested,
    digitalOnly,
    trimCode,
    trimKey: supportedTrim?.key || null,
    trimSupported: Boolean(supportedTrim),
    orientationCompatible: Boolean(supportedTrim && orientation === supportedTrim.orientation),
    expectedOrientation: supportedTrim?.orientation || "",
    bleedExplicit: bleed === "yes" || bleed === "no",
    binding, paperType, interiorColor,
    paperInteriorCompatible, settingsValid, missingSettings,
    settingsComplete: printRequested && Boolean(supportedTrim) &&
      orientation === supportedTrim.orientation && (bleed === "yes" || bleed === "no") &&
      binding === "paperback" && Boolean(paperType) && Boolean(interiorColor) && settingsValid,
    // Saved specification fields are not an authoritative print-production profile.
    productionProfileReady: false,
    printFilesEnabled: false,
  };
}

export default function NovelStoryPublicationPreparation({
  title = "", authorName = "", storyId = "", previewOnly = false, bookFormat = {}, frontCoverUrl = "", backCoverUrl = "",
  frontMatter = [], chapters = [], backMatter = [], sceneById,
  unassignedSceneIds = [], isFR = false,
  onOpenBookSetup, onOpenBookStructure, onOpenReview, onOpenProof,
}) {
  const readiness = novelPublicationPreparationChecks({
    bookFormat, frontCoverUrl, backCoverUrl, chapters, sceneById, unassignedSceneIds,
  });
  const printPlanning = novelPrintSpecificationsPlanning(bookFormat);
  const [sourceInventoryOpen, setSourceInventoryOpen] = useState(false);
  const [interiorOrderOpen, setInteriorOrderOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const sourceManifest = buildNovelProductionSourceManifest({
    title, frontMatter, chapters, backMatter, sceneById, unassignedSceneIds,
  });
  const interiorPlan = buildNovelInteriorProductionPlanV3({
    sourceManifest, printPlanning,
  });
  const physicalLayout = buildNovelInteriorPhysicalLayoutV3B({
    bookFormat, interiorPlan, bookTitle: title, authorName, frontMatter, backMatter,
  });
  const label = (en, fr) => isFR ? fr : en;
  const actionMap = {
    setup: onOpenBookSetup, "book-structure": onOpenBookStructure,
    review: onOpenReview, "publication-proof": onOpenProof,
  };
  const goTo = (key) => actionMap[key]?.();
  return (
    <section data-novel-publication-preparation="v1" style={{ display: "grid", gap: 16 }}>
      <header className="ns-card" style={{ padding: 22, borderRadius: 18, background: "#f3faf6",
        border: "1px solid #c9e4d6", borderLeft: "5px solid #047857" }}>
        <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.1em", color: "#047857" }}>
          {label("NEXTSCENES BOOK PRINT", "NEXTSCENES · LIVRE IMPRIMÉ")}
        </div>
        <h2 style={{ margin: "8px 0" }}>{label("Prepare the Novel for Publication", "Préparer le roman pour la publication")}</h2>
        <p style={{ margin: "0 0 8px", fontWeight: 750 }}>{clean(title)}</p>
        <p className="ns-card-meta" style={{ margin: 0 }}>
          {label(
            "Review your saved book, choose its format, and open a private preview. Technical production checks stay behind the scenes. Publication approval is not yet enabled.",
            "Contrôles de préparation inspirés de Textbook. Ordre : couverture avant → préliminaires → chapitres → éléments de fin → couverture arrière. Aucune publication ni export ici."
          )}
        </p>
      </header>

      <NovelPublicationSimpleOverviewV3C3
        title={title}
        authorName={authorName}
        storyId={storyId}
        previewOnly={previewOnly}
        frontMatter={frontMatter}
        chapters={chapters}
        backMatter={backMatter}
        readiness={readiness}
        sourceManifest={sourceManifest}
        printPlanning={printPlanning}
        isFR={isFR}
        onOpenReview={() => goTo("review")}
        onOpenBookSetup={() => goTo("setup")}
        onOpenProof={() => goTo("publication-proof")}
      />

      <button type="button" className="ns-button ns-button-outline"
        data-novel-writer-publication-advanced-v3c3="optional"
        aria-expanded={advancedOpen}
        onClick={() => setAdvancedOpen((open) => !open)}
        style={{ justifySelf: "start" }}>
        {advancedOpen
          ? label("Hide Advanced Publication Details", "Masquer les détails techniques")
          : label("Advanced Publication Details (Optional)", "Détails techniques (facultatif)")}
      </button>
      {advancedOpen ? (
        <div data-novel-publication-advanced-v3c3-content="optional"
          style={{ display: "grid", gap: 16 }}>

      <article className="ns-card" data-novel-publication-preparation-readiness="true"
        style={{ padding: 20, borderRadius: 18, background: "#f8fafc",
          border: "1px solid #cbd5e1", borderLeft: "5px solid #64748b" }}>
        <h3 style={{ margin: "0 0 6px", color: "#334155" }}>{label("Source Readiness", "Préparation des sources")} · {readiness.passed}/{readiness.total}</h3>
        <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
          {label("These checks describe saved sources, not literary approval or print-file certification.",
            "Ces vérifications concernent les sources enregistrées, pas la qualité littéraire ni un fichier prêt pour impression.")}
        </p>
        <div style={{ display: "grid", gap: 10 }}>
          {readiness.checks.map((check) => (
            <div key={check.id} data-novel-publication-preparation-check={check.id}
              style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", justifyContent: "space-between", padding: 14, border: "1px solid #dbe5df", borderRadius: 12, background: "#fff" }}>
              <div style={{ flex: "1 1 320px" }}>
                <strong>{check.label}</strong>
                <div className="ns-card-meta" style={{ marginTop: 5 }}>{check.detail}</div>
              </div>
              <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 850, color: check.ready ? "#047857" : "#92400e" }}>
                  {check.ready ? label("Source present", "Source présente") : label("Needs attention", "À compléter")}
                </span>
                <button className="ns-button ns-button-outline" type="button" onClick={() => goTo(check.destination)}>
                  {label("Open source", "Ouvrir la source")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </article>

      <article className="ns-card" data-novel-publication-preparation-assembly="true"
        style={{ padding: 20, borderRadius: 18, background: "#f8fafc",
          border: "1px solid #cbd5e1", borderLeft: "5px solid #64748b" }}>
        <h3 style={{ margin: "0 0 9px", color: "#334155" }}>{label("Saved Book Assembly", "Composition du livre enregistré")}</h3>
        <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
          {safeArray(frontMatter).length} Front Matter · {readiness.chapters} Chapters · {readiness.assignedScenes} assigned Canon Scenes · {safeArray(backMatter).length} Back Matter
        </p>
        <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
          {label("Publication format", "Format")}: <strong>{clean(bookFormat.publicationFormat) || "Not set"}</strong>
          {" · "}{label("Trim", "Coupe")}: <strong>{readiness.trim.label}</strong>
          {" · "}{label("Orientation", "Orientation")}: <strong>{clean(bookFormat.orientation) || "Not set"}</strong>
          {" · "}{label("Bleed", "Fond perdu")}: <strong>{clean(bookFormat.bleed) || "Not set"}</strong>
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="ns-button ns-button-outline" type="button" onClick={() => goTo("setup")}>{label("Book Setup", "Préparer le livre")}</button>
          <button className="ns-button ns-button-outline" type="button" onClick={() => goTo("book-structure")}>{label("Book Structure", "Structure du livre")}</button>
          <button className="ns-button ns-button-outline" type="button" onClick={() => goTo("review")}>{label("Review Saved Book", "Réviser le livre")}</button>
          <button className="ns-button ns-button-primary" type="button" onClick={() => goTo("publication-proof")}>{label("Open Book Layout Preview", "Ouvrir l’aperçu de mise en page")}</button>
        </div>
      </article>

      <article className="ns-card" data-novel-production-source-manifest="v1a"
        style={{ padding: 20, borderRadius: 18, background: "#f4fbfa",
          border: "1px solid #b5ded7", borderLeft: "5px solid #0f766e" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "0 0 5px", display: "flex", alignItems: "center",
              gap: 10, color: "#115e59" }}>
              <span aria-hidden="true" style={{ display: "inline-grid", placeItems: "center",
                flex: "0 0 32px", height: 32, borderRadius: 10,
                background: "#d7f0eb", color: "#115e59", fontSize: 13, fontWeight: 850 }}>01</span>
              {label("Production Source Inventory", "Inventaire des sources de production")}
            </h3>
            <p className="ns-card-meta" style={{ margin: 0 }}>
              {label("Source order only: Front Matter → Chapters → Back Matter. No manuscript lock, print files or approval.",
                "Ordre des sources seulement : préliminaires → chapitres → postliminaires. Aucun verrouillage ni approbation.")}
            </p>
          </div>
          <button type="button" className="ns-button ns-button-outline"
            aria-expanded={sourceInventoryOpen}
            onClick={() => setSourceInventoryOpen((open) => !open)}>
            {sourceInventoryOpen
              ? label("Hide Sources", "Masquer les sources")
              : label("Inspect Sources", "Inspecter les sources")}
          </button>
        </div>
        <p style={{ margin: "14px 0 0", fontWeight: 750, color: sourceManifest.readyForProductionContract ? "#047857" : "#92400e" }}>
          {sourceManifest.readyForProductionContract
            ? label("Saved source inventory consistent — not a final publication approval.",
                "Inventaire cohérent — pas encore une approbation finale.")
            : label("Saved source inventory needs attention before future final-file production.",
                "L'inventaire doit être complété avant la production finale.")}
          {" · "}{sourceManifest.sections.length} {label("sections", "sections")}
          {" · "}{sourceManifest.sceneCount} {label("assigned Canon Scenes", "scènes Canon affectées")}
          {" · "}{sourceManifest.issues.length} {label("source issues", "anomalies")}
        </p>
        {sourceInventoryOpen ? (
          <div style={{ marginTop: 14, maxHeight: 330, overflowY: "auto",
            border: "1px solid #dbe5df", borderRadius: 12, background: "#fff", padding: 12 }}>
            {sourceManifest.issues.map((item, index) => (
              <p key={item.code + "-" + index} role="alert"
                style={{ margin: "0 0 9px", color: "#92400e" }}>
                <strong>{item.location}:</strong> {item.description}
              </p>
            ))}
            {sourceManifest.sections.map((item, index) => (
              <div key={item.kind + "-" + item.id + "-" + index}
                style={{ display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 4px", borderTop: index ? "1px solid #eef2f7" : "none" }}>
                <span className="ns-card-meta" style={{ minWidth: 26 }}>{index + 1}.</span>
                <span style={{ flex: 1 }}>
                  <strong>{item.title}</strong>
                  <span className="ns-card-meta">{" · "}
                    {item.kind === "chapter" ? label("Chapter", "Chapitre")
                      : item.kind === "front" ? label("Front Matter", "Préliminaires")
                      : label("Back Matter", "Postliminaires")}
                    {item.kind === "chapter" ? " · " + item.sceneCount + " Scenes" : ""}
                    {" · "}{item.wordCount} {label("words", "mots")}
                  </span>
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </article>

      <NovelManuscriptSnapshotVerification
        storyId={storyId}
        previewOnly={previewOnly}
        isFR={isFR}
      />

      <article className="ns-card" data-novel-print-specifications-planning="v1"
        style={{ padding: 20, borderRadius: 18, background: "#f8fafc",
          border: "1px solid #cbd5e1", borderLeft: "5px solid #64748b" }}>
        <h3 style={{ margin: "0 0 7px", color: "#334155" }}>
          {label("Print Specifications · Planning Only", "Spécifications d'impression · Planification")}
        </h3>
        <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
          {label("Read-only comparison with the shared paperback print-profile requirements. It does not generate a production profile, measure a final interior or approve files.",
            "Comparaison en lecture seule avec les conditions de fabrication du livre broché. Aucune validation ni production de fichiers.")}
        </p>
        {printPlanning.printRequested ? (
          <div data-novel-print-specifications-status="incomplete" style={{ display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>
              <strong>{label("Saved choices", "Choix enregistrés")}:</strong>{" "}
              {readiness.trim.label} · {clean(bookFormat.orientation) || label("Orientation not selected", "Orientation à choisir")}
              {" · "}{label("Bleed", "Fond perdu")}: {clean(bookFormat.bleed) || label("Not selected", "Non renseigné")}
            </p>
            <p style={{ margin: 0, color: printPlanning.trimSupported && printPlanning.orientationCompatible && printPlanning.bleedExplicit
              ? "#334155" : "#92400e" }}>
              <strong>{label("Paperback compatibility", "Compatibilité livre broché")}:</strong>{" "}
              {!printPlanning.trimSupported
                ? label("This saved trim is not supported by the current shared print-profile engine.",
                    "Ce format de coupe n'est pas pris en charge par le moteur actuel.")
                : !printPlanning.orientationCompatible
                ? label("This trim needs " + printPlanning.expectedOrientation + " orientation for the current print profile.",
                    "L'orientation enregistrée ne correspond pas au format de coupe.")
                : !printPlanning.bleedExplicit
                ? label("Choose Yes or No for bleed in Book Setup.", "Choisir Oui ou Non pour le fond perdu.")
                : label("Saved trim, orientation and bleed are compatible for planning only.",
                    "Coupe, orientation et fond perdu compatibles pour la planification uniquement.")}
            </p>
            <p style={{ margin: 0 }}>
              <strong>{label("Saved print settings", "Réglages d'impression enregistrés")}:</strong>{" "}
              {label("Binding", "Reliure")}: {printPlanning.binding || label("Not selected", "Non renseigné")}
              {" · "}{label("Paper", "Papier")}: {printPlanning.paperType || label("Not selected", "Non renseigné")}
              {" · "}{label("Interior", "Intérieur")}: {printPlanning.interiorColor || label("Not selected", "Non renseigné")}
            </p>
            <p style={{ margin: 0, color: printPlanning.settingsComplete ? "#047857" : "#92400e" }}>
              <strong>{label("Print settings", "Réglages d'impression")}:</strong>{" "}
              {!printPlanning.settingsValid
                ? label("Incompatible paper or interior colour — correct this in Book Setup.",
                    "Combinaison de papier et de couleur intérieure incompatible.")
                : printPlanning.missingSettings.length
                ? label("Still required: " + printPlanning.missingSettings.join(", ") + ".",
                    "Il reste des choix obligatoires : " + printPlanning.missingSettings.join(", ") + ".")
                : printPlanning.settingsComplete
                ? label("Saved choices are compatible for planning; print production remains disabled.",
                    "Réglages enregistrés compatibles pour la planification ; la production reste désactivée.")
                : label("Check trim, orientation and bleed compatibility above.",
                    "Vérifier la compatibilité du format, de l'orientation et du fond perdu.")}
            </p>
          </div>
        ) : (
          <p data-novel-print-specifications-status="not-applicable" style={{ margin: 0 }}>
            {printPlanning.digitalOnly
              ? label("Digital-only book: paperback specifications do not apply.",
                  "Livre numérique uniquement : ces spécifications ne s'appliquent pas.")
              : label("Select a print publication format in Book Setup to assess paperback specifications.",
                  "Choisir un format imprimé dans la préparation du livre pour examiner les spécifications.")}
          </p>
        )}
        <p className="ns-card-meta" style={{ margin: "12px 0" }}>
          {label("Production print profile not established. Source Readiness 5/5 is not print-file approval.",
            "Profil d'impression non établi. La disponibilité des sources ne vaut pas approbation des fichiers.")}
        </p>
        <button className="ns-button ns-button-outline" type="button" onClick={() => goTo("setup")}>
          {label("Open Book Setup", "Ouvrir la préparation du livre")}
        </button>
      </article>

      <article className="ns-card" data-novel-interior-production-plan="v3a"
        style={{ padding: 20, borderRadius: 18, background: "#f8fafc",
          border: "1px solid #cbd5e1", borderLeft: "5px solid #64748b" }}>
        <h3 style={{ margin: "0 0 7px", color: "#334155" }}>
          {label("Interior Production · Layout Plan (V3A)", "Production intérieure · Plan de mise en page (V3A)")}
        </h3>
        <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
          {label("Read-only plan derived from saved Novel sources. It follows the proven Textbook new-page start pattern but does not measure, paginate, approve or export a production interior.",
            "Plan en lecture seule issu des sources enregistrées. Aucune pagination physique, approbation ou export.")}
        </p>
        <p style={{ margin: "0 0 10px" }}>
          <strong>{label("Saved interior order", "Ordre intérieur enregistré")}:</strong>{" "}
          {interiorPlan.frontMatterCount} {label("Front Matter", "Préliminaires")} {" → "}
          {interiorPlan.chapterCount} {label("Chapters", "Chapitres")} {" → "}
          {interiorPlan.backMatterCount} {label("Back Matter", "Postliminaires")}
          {" · "}{interiorPlan.assignedCanonScenes} {label("assigned Canon Scenes", "scènes Canon affectées")}
        </p>
        <p style={{ margin: "0 0 10px" }}>
          <strong>{label("Proposed page starts", "Débuts de page prévus")}:</strong>{" "}
          {label("Each Chapter and Book Matter section starts on a new page. Scenes continue within their Chapter in saved Canon order. Right-hand (recto) starts and inserted blank pages are not assumed.",
            "Chaque chapitre et élément du livre commence sur une nouvelle page. Les scènes suivent l'ordre Canon enregistré. Aucun recto ni page blanche n'est imposé.")}
        </p>
        <p data-novel-interior-planning-inputs={
            interiorPlan.planningInputsCompatible ? "compatible" : "needs-attention"}
          style={{ margin: "0 0 10px", fontWeight: 700,
            color: interiorPlan.planningInputsCompatible ? "#047857" : "#92400e" }}>
          {interiorPlan.digitalOnly
            ? label("Digital-only edition: physical interior print production does not apply.",
                "Édition numérique : production intérieure imprimée non applicable.")
            : interiorPlan.planningInputsCompatible
            ? label("Saved source order and print choices are compatible for planning only.",
                "Sources et choix d'impression compatibles pour la planification uniquement.")
            : label("Interior production inputs need attention: confirm the saved source inventory and complete compatible print specifications.",
                "Vérifiez les sources enregistrées et complétez les spécifications d'impression.")}
        </p>
        <p className="ns-card-meta" style={{ margin: "0 0 10px" }}>
          {label("Margins and gutter: final preflight must use measured pagination and the shared page-count-dependent margin policy. No production margin profile has been adopted.",
            "Marges et gouttière : vérification finale après pagination mesurée. Aucun profil de production adopté.")}
        </p>
        <p className="ns-card-meta" style={{ margin: "0 0 12px" }}>
          {label("Still pending: verified production snapshot authority, measured Interior PDF, final page count, print preflight and full-wrap cover/spine geometry. Browser proof pages are not the final print page count.",
            "À venir : copie source vérifiée, PDF intérieur mesuré, nombre de pages final, contrôle d'impression et dos de couverture. L'épreuve navigateur ne donne pas le nombre de pages final.")}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button type="button" className="ns-button ns-button-outline"
            data-novel-interior-plan-inspect="true" aria-expanded={interiorOrderOpen}
            onClick={() => setInteriorOrderOpen((open) => !open)}>
            {interiorOrderOpen ? label("Hide Interior Order", "Masquer l'ordre intérieur")
              : label("Inspect Interior Order", "Voir l'ordre intérieur")}
          </button>
          <button type="button" className="ns-button ns-button-outline"
            onClick={() => goTo("publication-proof")}>
            {label("Open Book Layout Preview", "Ouvrir l’aperçu de mise en page")}
          </button>
        </div>
        {interiorOrderOpen ? (
          <div data-novel-interior-plan-order="true"
            style={{ marginTop: 14, maxHeight: 300, overflowY: "auto", padding: 12,
              background: "#fff", border: "1px solid #dbe5df", borderRadius: 12 }}>
            {interiorPlan.units.length === 0 ? (
              <p className="ns-card-meta" style={{ margin: 0 }}>
                {label("No saved interior sections yet.", "Aucune section intérieure enregistrée.")}
              </p>
            ) : interiorPlan.units.map((unit) => (
              <div key={unit.kind + "-" + unit.sourceId + "-" + unit.position}
                style={{ padding: "8px 4px", borderBottom: "1px solid #eef2f7" }}>
                <strong>{unit.position}. {unit.title || label("Untitled", "Sans titre")}</strong>
                <div className="ns-card-meta">
                  {unit.kind === "chapter" ? label("Chapter", "Chapitre")
                    : unit.kind === "front" ? label("Front Matter", "Préliminaires")
                    : label("Back Matter", "Postliminaires")}
                  {" · "}{label("Start: next page (planned)", "Début : page suivante (prévu)")}
                  {unit.kind === "chapter"
                    ? " · " + unit.sceneCount + " " + label("Canon Scenes", "scènes Canon")
                    : ""}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div data-novel-interior-physical-layout-v3b="study-only"
          style={{ marginTop: 16, padding: 14, border: "1px solid #cbd5e1",
            borderRadius: 12, background: "#ffffff" }}>
          <h4 style={{ margin: "0 0 8px", color: "#334155" }}>
            {label("Physical Layout & Typography · Study Only (V3B)",
              "Mise en page physique et typographie · Étude (V3B)")}
          </h4>
          <p className="ns-card-meta" style={{ margin: "0 0 9px" }}>
            {label("All writer-created Front Matter and Back Matter sections are included in their saved order, not a fixed template. Their complete structured content remains attached to the original saved entries; no edits or production rendering occur here.",
              "Toutes les sections ajoutées par l'auteur sont prises en compte dans l'ordre enregistré, sans modèle imposé. Aucun rendu final ici.")}
          </p>
          <p data-novel-interior-v3b-source-coverage={
              physicalLayout.sourceBindingsComplete ? "complete" : "needs-attention"}
            style={{ margin: "0 0 10px",
              color: physicalLayout.sourceBindingsComplete ? "#047857" : "#92400e" }}>
            <strong>{label("Saved matter coverage", "Couverture des sections enregistrées")}:</strong>{" "}
            {physicalLayout.savedFrontMatterCount} {label("Front Matter", "Préliminaires")}
            {" · "}{physicalLayout.savedBackMatterCount} {label("Back Matter", "Postliminaires")}
            {" · "}{physicalLayout.sourceBindingsComplete
              ? label("all saved sections linked to original source", "toutes les sections liées à leur source")
              : label("source mapping incomplete — do not proceed", "correspondance incomplète — ne pas continuer")}
          </p>
          {physicalLayout.titlePageCount ? (
            <p style={{ margin: "0 0 8px" }}>
              <strong>{label("Title page", "Page de titre")}:</strong>{" "}
              {label("Book title alone on its own page; no subtitle or author byline added.",
                "Titre du livre seul sur sa page ; ni sous-titre ni signature ajoutés.")}
            </p>
          ) : null}
          {physicalLayout.subtitlePageCount ? (
            <p style={{ margin: "0 0 8px" }}>
              <strong>{label("Subtitle page", "Page du sous-titre")}:</strong>{" "}
              {label("Separate saved Subtitle section, with author identification where provided. Its saved content is not merged with the Title page.",
                "Sous-titre sur une page distincte, avec le nom de l'auteur s'il est fourni. Aucun regroupement avec le titre.")}
            </p>
          ) : null}
          <p style={{ margin: "0 0 8px" }}>
            <strong>{label("Additional sections", "Sections supplémentaires")}:</strong>{" "}
            {label("Dedication, Foreword, Preface, Acknowledgements, Appendices and any other writer-added items keep their own order and new-page starts whenever present.",
              "Toute autre section créée conserve son ordre et commence sur une nouvelle page.")}
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong>{label("Physical trim", "Format physique")}:</strong>{" "}
            {physicalLayout.geometrySupported
              ? physicalLayout.trimWidthInches + " × " + physicalLayout.trimHeightInches + " " +
                label("inches", "pouces")
              : label("Not available for the saved trim and orientation.", "Format ou orientation non pris en charge.")}
            {" · "}
            <strong>{label("Classic text region study", "Zone de texte proposée")}:</strong>{" "}
            {physicalLayout.geometrySupported
              ? physicalLayout.studyContentWidthInches + " × " +
                physicalLayout.studyContentHeightInches + " " + label("inches", "pouces")
              : label("Not established", "Non établie")}
          </p>
          <p className="ns-card-meta" style={{ margin: "0 0 8px" }}>
            {label("Reference typography only: Georgia 11 pt, 14 pt line height. Reference margins: inside 0.75 in, outside 0.5 in, top/bottom 0.75 in. These are inherited classic study values, not approved production geometry or an adopted typographic override.",
              "Référence uniquement : Georgia 11 pt, interligne 14 pt ; marges classiques indicatives. Aucun profil de fabrication adopté.")}
          </p>
          <p className="ns-card-meta" style={{ margin: 0, color: "#92400e" }}>
            {label("No final physical print page count has been independently verified. V3C2 provides a separate provisional physical-text study only; no approved interior PDF, manuscript lock, spine measurement or publication authority.",
              "Aucune pagination imprimée définitive vérifiée. V3C2 fournit uniquement une étude provisoire du texte ; aucun PDF final, verrouillage, dos de couverture ni autorisation de publication.")}
          </p>
        </div>
      </article>

      <NovelPhysicalPaginationStudyV3C2Panel
        title={title}
        authorName={authorName}
        storyId={storyId}
        previewOnly={previewOnly}
        bookFormat={bookFormat}
        frontMatter={frontMatter}
        chapters={chapters}
        backMatter={backMatter}
        sceneById={sceneById}
        isFR={isFR}
      />

      <article className="ns-card" data-novel-publication-preparation-production-gate="not-enabled"
        style={{ padding: 20, borderRadius: 18, background: "#fffbf4",
          border: "1px solid #edd4a8", borderLeft: "5px solid #b45309" }}>
        <h3 style={{ margin: "0 0 9px", display: "flex", alignItems: "center",
          gap: 10, color: "#92400e" }}>
          <span aria-hidden="true" style={{ display: "inline-grid", placeItems: "center",
            flex: "0 0 32px", height: 32, borderRadius: 10,
            background: "#fff0cc", color: "#92400e", fontSize: 13, fontWeight: 850 }}>03</span>
          {label("Final Print Files — Separate Approval", "Fichiers d’impression finaux — Approbation distincte")}
        </h3>
        <p className="ns-card-meta" style={{ margin: "0 0 10px" }}>
          {label(
            "This preparation panel checks the saved manuscript and print settings. Browser layout measurements are provisional. Final approval of an exact interior PDF is a separate, restricted step when enabled for a specific book; it does not approve a cover or publish the book.",
            "Cette page contrôle le manuscrit enregistré et les paramètres d’impression. Les mesures du navigateur restent provisoires. L’approbation du PDF intérieur exact est une étape distincte et réservée à un livre autorisé ; elle n’approuve pas la couverture et ne publie pas le livre."
          )}
        </p>
        <p style={{ margin: 0, fontWeight: 750, color: "#92400e" }}>
          {label("This preparation panel does not lock the manuscript, approve a final PDF, export it or publish the book. Final Interior Approval is controlled separately.",
            "Cette page ne verrouille pas le manuscrit et ne permet ni l’approbation d’un PDF final, ni son exportation, ni sa publication. L’approbation finale est contrôlée séparément.")}
        </p>
      </article>
        </div>
      ) : null}
    </section>
  );
}
